import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import Composer from './Composer';
import ConversationList from './ConversationList';
import MessageBubble from './MessageBubble';
import ModelPicker from './ModelPicker';
import SkillCards from './SkillCards';
import {
  createConversation,
  deleteConversation as deleteConversationApi,
  fetchConfig,
  fetchSkills,
  getConversation,
  listConversations,
} from './api';
import useChatStream from './useChatStream';

const DEFAULT_MAX_CHARS = 20000;

const AIChat = () => {
  const [config, setConfig] = useState(null);
  const [gateError, setGateError] = useState('');
  const [loading, setLoading] = useState(true);

  const [skills, setSkills] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [model, setModel] = useState('');

  const scrollRef = useRef(null);
  // The streaming assistant turn is rebuilt on every frame; a ref keeps the
  // frame handler stable so the stream hook is not torn down mid-answer.
  const liveRef = useRef({ content: '', tools: [] });

  const scrollToBottom = useCallback(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, []);

  const replaceLastAssistant = useCallback((patch) => {
    setMessages((current) => {
      const next = current.slice();
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].role === 'assistant') {
          next[i] = { ...next[i], ...patch };
          return next;
        }
      }
      return next;
    });
  }, []);

  const handleFrame = useCallback(
    ({ event, data }) => {
      const live = liveRef.current;

      if (event === 'delta') {
        live.content += data.text;
        replaceLastAssistant({ content: live.content, pending: false });
      } else if (event === 'tool') {
        const existing = live.tools.findIndex(
          (tool) => tool.name === data.name && tool.status === 'running'
        );
        if (data.status === 'running') {
          live.tools = live.tools.concat([{ name: data.name, status: 'running' }]);
        } else if (existing >= 0) {
          live.tools = live.tools.slice();
          live.tools[existing] = { name: data.name, status: data.status, ms: data.ms };
        } else {
          live.tools = live.tools.concat([
            { name: data.name, status: data.status, ms: data.ms },
          ]);
        }
        replaceLastAssistant({ tool_calls: live.tools });
      } else if (event === 'error') {
        replaceLastAssistant({ error: data.message, pending: false });
      } else if (event === 'done') {
        replaceLastAssistant({ pending: false });
        // The title is set server-side from the first question; refresh the
        // sidebar so a new thread stops saying "Untitled".
        listConversations()
          .then((body) => setConversations(body.results || []))
          .catch(() => {});
      }
      scrollToBottom();
    },
    [replaceLastAssistant, scrollToBottom]
  );

  const { send, stop, streaming } = useChatStream({ onFrame: handleFrame });

  // -- bootstrap ---------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [configBody, skillsBody, conversationsBody] = await Promise.all([
          fetchConfig(),
          fetchSkills(),
          listConversations(),
        ]);
        if (cancelled) return;
        setConfig(configBody);
        setModel(configBody.model);
        setSkills(skillsBody.results || []);
        setConversations(conversationsBody.results || []);
      } catch (err) {
        if (cancelled) return;
        setGateError(
          err.status === 403
            ? 'This assistant is limited to internal staff accounts.'
            : err.message
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openConversation = useCallback(async (id) => {
    setActiveId(id);
    setMessages([]);
    try {
      const body = await getConversation(id);
      setMessages(body.messages || []);
      if (body.model) setModel(body.model);
    } catch (err) {
      setMessages([{ role: 'assistant', content: '', error: err.message }]);
    }
  }, []);

  const startNewConversation = useCallback(() => {
    setActiveId(null);
    setMessages([]);
    setDraft('');
  }, []);

  const removeConversation = useCallback(
    async (id) => {
      try {
        await deleteConversationApi(id);
      } catch (err) {
        return;
      }
      setConversations((current) => current.filter((c) => c.id !== id));
      if (id === activeId) startNewConversation();
    },
    [activeId, startNewConversation]
  );

  // -- sending -----------------------------------------------------------

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text || streaming) return;

    let conversationId = activeId;
    if (!conversationId) {
      try {
        const created = await createConversation();
        conversationId = created.id;
        setActiveId(created.id);
        setConversations((current) => [created].concat(current));
      } catch (err) {
        setMessages((current) =>
          current.concat([{ role: 'assistant', content: '', error: err.message }])
        );
        return;
      }
    }

    liveRef.current = { content: '', tools: [] };
    setDraft('');
    setMessages((current) =>
      current.concat([
        { role: 'user', content: text },
        { role: 'assistant', content: '', tool_calls: [], pending: true },
      ])
    );
    scrollToBottom();

    await send(conversationId, text, model);
  }, [activeId, draft, model, scrollToBottom, send, streaming]);

  // -- render ------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (gateError) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 10, px: 3 }}>
        <Alert severity="warning">{gateError}</Alert>
      </Box>
    );
  }

  const brandName = config && config.brand ? config.brand.name : null;

  return (
    // ComplianceFooter is position:fixed and ~34px tall app-wide; reserving it
    // here keeps the composer from sitting underneath it.
    <Box sx={{ display: 'flex', height: 'calc(100vh - 34px)', bgcolor: 'background.paper' }}>
      <ConversationList
        conversations={conversations}
        activeId={activeId}
        onSelect={openConversation}
        onCreate={startNewConversation}
        onDelete={removeConversation}
      />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'baseline',
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>Assistant</Typography>
          {brandName ? (
            <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>
              {brandName}
            </Typography>
          ) : null}
          <Box sx={{ flex: 1 }} />
          {/* Locked mid-answer: the model is already committed for this turn,
              and changing it would misreport which one produced the text. */}
          <ModelPicker
            models={config.models}
            value={model}
            onChange={setModel}
            disabled={streaming}
          />
        </Box>

        <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 3 }}>
          {messages.length === 0 ? (
            <SkillCards
              skills={skills}
              brandName={brandName}
              onPick={(skill) => setDraft(skill.prompt)}
            />
          ) : (
            <Box sx={{ maxWidth: 820, mx: 'auto', py: 3 }}>
              {messages.map((message, index) => (
                // Persisted messages have ids; the two optimistic rows added on
                // send do not, so the index is part of the key.
                <MessageBubble key={message.id || `live-${index}`} message={message} />
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Box sx={{ maxWidth: 820, mx: 'auto' }}>
            <Composer
              value={draft}
              onChange={setDraft}
              onSend={handleSend}
              onStop={stop}
              streaming={streaming}
              maxChars={(config && config.max_message_chars) || DEFAULT_MAX_CHARS}
            />
            <Typography
              sx={{ color: 'text.secondary', fontSize: 11.5, mt: 1, textAlign: 'center' }}
            >
              Reads live platform data, scoped to your brand. Check numbers before sharing them
              externally.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AIChat;
